import { defineComponent as _defineComponent } from 'vue'
import type { Route } from 'vue-router';
import type { ValueOf } from '@/helpers/types';
import type { TrailerClip } from '@/interfaces/trailerClip';
import type { YoutubePlayerOptions } from '@/interfaces/components/youtube-player';

import { PlayerIcon } from '@/enums/player-icon';
import YouTubePlayer from 'youtube-player';
import VideoPlayerWrapper from '@/ui-components/VideoPlayerWrapper.vue';

import { computed, onMounted, ref, watch } from 'vue';

type YouTubePlayerType = ReturnType<typeof YouTubePlayer> & {
	off: (event: any) => void;
};

type YoutubePlayerStatus = ValueOf<typeof YoutubePlayerStatus>;

export default /*#__PURE__*/_defineComponent({
  __name: 'YoutubePlayer',
  props: {
    clip: null,
    options: null,
    source: { default: '' },
    width: { default: '100%' },
    height: null,
    isActive: { type: Boolean },
    activeRoute: null,
    isBackdrop: { type: Boolean },
    playIconType: null,
    isDisneyPlusOffer: { type: Boolean }
  },
  emits: ["setActiveClipId", "onPlaying", "onPaused", "onEnded", "onBuffering"],
  setup(__props: any, { emit }: { emit: ({
	(e: 'setActiveClipId', clipId: string | null): void;
	(e: 'onPlaying', clip: TrailerClip): void;
	(e: 'onPaused', clip: TrailerClip): void;
	(e: 'onEnded', clip: TrailerClip): void;
	(e: 'onBuffering', clip: TrailerClip): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { clip: TrailerClip, options: YoutubePlayerOptions, source: string, width: number | string, height?: number | string, isActive?: boolean, activeRoute?: Route, isBackdrop?: boolean, playIconType?: PlayerIcon, isDisneyPlusOffer?: boolean };





const YoutubePlayerStatus = {
	Unstarted: '-1',
	Ended: 0,
	Playing: 1,
	Paused: 2,
	Buffering: 3,
	Cued: 5,
} as const;

const YoutubePlayerEvent = {
	Ready: 'ready',
	StateChange: 'stateChange',
} as const;

const player = ref<YouTubePlayerType | null>(null);

const playerEventListeners = ref<(YouTubePlayerType['on'] | void)[]>([]);

watch(
	() => props.isActive,
	() => {
		if (player.value && !props.isActive) {
			player.value.pauseVideo();
		}
	}
);

function destroyPlayer() {
	if (playerEventListeners.value?.length) {
		playerEventListeners.value.forEach(playerEventListener => {
			player.value?.off(playerEventListener);
		});
	}

	if (props.isActive) {
		emit('setActiveClipId', null);
	}

	if (player.value) {
		player.value.destroy();
		player.value = null;
	}
}

watch(
	() => props.activeRoute,
	() => {
		destroyPlayer();
	}
);

const previewImageUrl = computed(() =>
	props.clip ? `https://img.youtube.com/vi/${props.clip.externalId}/mqdefault.jpg` : ''
);
const playerOptions = computed<YoutubePlayerOptions>(() => {
	return {
		autohide: 1,
		iv_load_policy: 3,
		modestbranding: 1,
		rel: 0,
		mute: 0,
		...props.options,
	};
});

const playerId = computed(() => {
	return `youtube-player-${props.clip.externalId}-${props.source}`;
});

function onStateChange(event: { data: YoutubePlayerStatus }) {
	if (event.data === YoutubePlayerStatus.Playing) {
		emit('setActiveClipId', props.clip.externalId);
		emit('onPlaying', props.clip);
	}

	if (event.data === YoutubePlayerStatus.Paused) {
		emit('onPaused', props.clip);
	}

	if (event.data === YoutubePlayerStatus.Ended) {
		emit('setActiveClipId', null);
		emit('onEnded', props.clip);
	}

	if (event.data === YoutubePlayerStatus.Buffering) {
		emit('onBuffering', props.clip);
	}
}

function play() {
	if (player.value) {
		player.value.playVideo();
	} else {
		player.value = YouTubePlayer(playerId.value, {
			...(props.width ? { width: props.width } : {}),
			...(props.height ? { height: props.height } : {}),
			videoId: props.clip.externalId,
			playerVars: playerOptions.value,
		}) as YouTubePlayerType;

		playerEventListeners.value.push(
			player.value.on(YoutubePlayerEvent.Ready, () => {
				emit('setActiveClipId', props.clip.externalId);
				player.value?.playVideo();
			})
		);

		playerEventListeners.value.push(
			player.value.on(YoutubePlayerEvent.StateChange, (event: any) => {
				onStateChange(event);
			})
		);
	}
}

onMounted(() => {
	if (props.isActive) {
		setTimeout(() => {
			play();
		}, 0);
	}
});

return { __sfc: true,props, emit, YoutubePlayerStatus, YoutubePlayerEvent, player, playerEventListeners, destroyPlayer, previewImageUrl, playerOptions, playerId, onStateChange, play, VideoPlayerWrapper }
}

})